import { Component, computed, effect, inject, OnInit, resource, signal, ChangeDetectionStrategy } from '@angular/core';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { GenericModalService } from '../../../shared/generic-modal/generic-modal.service';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { LoadingPanelService } from '../../../shared/loading-panel/loading-panel.service';
import { AlertService } from '../../services/alert.service';
import { AlertConfigurationStatus } from '../../models/alert-configuration';
import { AlertStatusGridComponent } from '../alert-status-grid/alert-status-grid.component';
import { AlertAbsenceDialogComponent } from '../alert-absence-dialog/alert-absence-dialog.component';
import { AbsenceConfigurationPresentation } from '../../models/absence-configuration-presentation';
import { ToastService, ToastType } from '../../../shared/toast/toast.service';

@Component({
  templateUrl: './alert-status.component.html',
  styleUrls: ['./alert-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SvgIconComponent, PermissionRequiredDirective, AlertStatusGridComponent]
})
export class AlertStatusComponent implements OnInit {
  private readonly alertService = inject(AlertService);
  private readonly loadingPanelService = inject(LoadingPanelService);
  private readonly toastService = inject(ToastService);
  private readonly modalService = inject(GenericModalService);

  readonly alertConfigurationStatuses = signal<AlertConfigurationStatus[]>([]);
  readonly selectedAlertConfigurationStatuses = signal<AlertConfigurationStatus[]>([]);

  private readonly globalAlertAbsenceResource = resource({
    params: () => ({}),
    loader: () => this.alertService.getGlobalAlertAbsence(),
  });
  readonly globalAlertAbsence = computed<AbsenceConfigurationPresentation>(() => {
    const absence = this.globalAlertAbsenceResource.value();
    const now = Date.now();
    return absence?.ToDateTime && absence.ToDateTime.getTime() >= now ? absence : { FromDateTime: null, ToDateTime: null, Note: '' };
  });

  private readonly transitionTick = signal(0);
  readonly nextGlobalAlertAbsenceTransitionAt = computed(() => {
    this.transitionTick();

    const absence = this.globalAlertAbsence();
    if (!absence?.FromDateTime || !absence.ToDateTime) {
      return null;
    }

    const now = Date.now();
    const startTime = absence.FromDateTime.getTime();
    const endTime = absence.ToDateTime.getTime();

    return now < startTime ? startTime : now < endTime ? endTime : null;
  });

  private readonly scheduleTransitionEffect = effect(onCleanup => {
    const nextTransitionAt = this.nextGlobalAlertAbsenceTransitionAt();
    if (nextTransitionAt) {
      const timeoutId = setTimeout(() => this.transitionTick.update(value => value + 1), Math.max(0, nextTransitionAt - Date.now()));
      onCleanup(() => clearTimeout(timeoutId));
    }
  });

  readonly isGlobalAlertAbsenceActive = computed(() => {
    this.transitionTick();

    // Active means the current time is inside the configured absence window (inclusive).
    const absence = this.globalAlertAbsence();
    if (absence) {
      const now = new Date();
      return absence.FromDateTime && absence.ToDateTime && absence.FromDateTime <= now && now < absence.ToDateTime;
    }
    return false;
  });

  readonly isGlobalAlertAbsenceScheduled = computed(() => {
    this.transitionTick();

    // Scheduled means the absence window starts strictly in the future.
    // This is intentionally exclusive with "active" so only one state can be true.
    const absence = this.globalAlertAbsence();
    if (absence) {
      const now = new Date();
      return absence.FromDateTime && absence.ToDateTime && now < absence.FromDateTime && now < absence.ToDateTime;
    }
    return false;
  });

  ngOnInit(): void {
    void this.refresh();
  }

  async refresh(): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Loading alert statuses...');
      this.globalAlertAbsenceResource.reload();
      this.alertConfigurationStatuses.set(await this.alertService.getAlertConfigurationStatuses());
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  async enableAlerts(): Promise<void> {
    const requestText = `Do you want to enable ${this.selectedAlertConfigurationStatuses().length} alert configuration(s)?`;

    try {
      await this.modalService.confirmDirect('Enable Alert Configuration', requestText);
      const selectedAlertConfigurationIds = this.selectedAlertConfigurationStatuses().map(x => x.AlertConfigurationId);
      await this.alertService.enableAlertConfigurations(selectedAlertConfigurationIds);
      await this.refresh();
    }
    catch {
      // User cancelled the confirmation dialog.
    }
  }

  async disableAlerts(): Promise<void> {
    const requestText = `Do you want to disable ${this.selectedAlertConfigurationStatuses().length} alert configuration(s)?`;

    try {
      await this.modalService.confirmDirect('Disable Alert Configuration', requestText);
      const selectedAlertConfigurationIds = this.selectedAlertConfigurationStatuses().map(x => x.AlertConfigurationId);
      await this.alertService.disableAlertConfigurations(selectedAlertConfigurationIds);
      await this.refresh();
    }
    catch {
      // User cancelled the confirmation dialog.
    }
  }

  async cancelAlerts(): Promise<void> {
    const requestText = `Do you want to cancel ${this.selectedAlertConfigurationStatuses().length} alert(s) for the rest of the day?`;

    try {
      await this.modalService.confirmDirect('Cancel Alert', requestText);
      const selectedAlertConfigurationIds = this.selectedAlertConfigurationStatuses().map((x) => x.AlertConfigurationId);
      await this.alertService.cancelAlerts(selectedAlertConfigurationIds);
      await this.refresh();
    }
    catch {
      // User cancelled the confirmation dialog.
    }
  }

  onSelectedAlertConfigurationChange(selectedAlertConfigurationStatuses: AlertConfigurationStatus[]): void {
    this.selectedAlertConfigurationStatuses.set(selectedAlertConfigurationStatuses ?? []);
  }

  async absenceSchedule(): Promise<void> {
    try {
      const result = await this.modalService.openComponentWithSignalInputs<AbsenceConfigurationPresentation, AlertAbsenceDialogComponent>(AlertAbsenceDialogComponent, undefined, {
        absenceStart: this.globalAlertAbsence().FromDateTime,
        absenceEnd: this.globalAlertAbsence().ToDateTime,
        absenceNote: this.globalAlertAbsence().Note
      });
      try {
        if (result) {
          // Set or update the global alert absence configuration based on the dialog result
          await this.alertService.setGlobalAlertAbsence(result);
          this.toastService.showToast('Alert absence saved successfully...', ToastType.success);
        }
        else {
          // If the dialog was closed without a null result, treat it as a cancellation of current absence, which ensures any currently active absence is ended.
          const endDate = new Date(Math.min(this.globalAlertAbsence().ToDateTime?.getTime() ?? Date.now(), Date.now()));
          endDate.setHours(0, 0, 0, 0);
          const startDate = new Date(Math.min(this.globalAlertAbsence().FromDateTime?.getTime() ?? Date.now(), endDate.getTime()));
          const absence: AbsenceConfigurationPresentation = { FromDateTime: startDate, ToDateTime: endDate, Note: this.globalAlertAbsence().Note };
          await this.alertService.setGlobalAlertAbsence(absence);
          this.toastService.showToast('Alert absence cleared successfully...', ToastType.success);
        }
        this.globalAlertAbsenceResource.reload();
      }
      catch {
        this.toastService.showToast('Failed to update alert absence...', ToastType.error);
      }
    }
    catch (error) {
      if (error) console.error('Error opening alert absence dialog:', error);
    }
  }
}
